import { reduce, map } from 'ramda';

/**
 * Combine an array of overrides of different types into a single one, ordered
 * from lowest to highest precedence
 */
export const mergeOverrides = <T extends (Override<string>)[]>(
  overrides: T,
): MergeResult<T> => {
  return reduce(
    (acc, override) => combineOverrides(acc, override) as MergeResult<T>,
    {
      precedence: [] as const,
      shouldApply: {},
    } as MergeResult<T>,
    overrides,
  );
};

/**
 * Describe an override configuration: the order of override that could be
 * applied, from lowest to highest, and whether each one should be applied.
 */
export interface Override<T extends string> {
  /**
   * The order of override type that should apply from lowest to highest
   * precedence.
   */
  precedence: readonly T[];
  /**
   * An object whose key is the override type and value is a boolean on whether
   * it should be applied.
   */
  shouldApply: {
    // Detect if T is `string`. If T is string, Exclude<string, T> == never
    // If T is a literal string/string union, Exclude<string, T> is still string.
    // So if T is a union, we require that all keys in T be required.
    // Otherwise, if we are mapping string to boolean, some string maybe undefined.
    [K in T]: T extends Exclude<string, T> ? boolean : boolean | undefined;
  };
}

/**
 * Combine two override objects A and B into one, where everything in B
 * takes precedence over everything in A.
 */
function combineOverrides<A extends string, B extends string>(
  a: Override<A>,
  b: Override<B>,
): Override<A | B> {
  return {
    precedence: [...a.precedence, ...b.precedence],
    shouldApply: { ...a.shouldApply, ...b.shouldApply },
  };
}

type UnwrapOverride<T> = T extends Override<infer K> ? K : never;

/**
 * Combine a tuple of type [Override<A>, Override<B>, Override<C>, ...] into
 * a single Override<A | B | C | ...>
 */
type MergeResult<T extends (Override<string>)[]> = T extends (infer U)[]
  ? Override<UnwrapOverride<U>>
  : never;

/**
 * Return an array of translation keys to use in the order that would apply the
 * provided overrides with the given precedence.
 *
 * @param key translation key
 * @param override An override configuration
 */
export const fallBackForKey = <T extends Override<string>>(
  key: string,
  override: T,
) => {
  return reduce(
    (acc, item) => {
      if (!override.shouldApply[item]) return acc;
      const newItems = map(previousItem => {
        return `override.${item}.${previousItem}`;
      }, acc);
      return [...newItems, ...acc];
    },
    [key],
    override.precedence as string[],
  );
};
